import { supabase } from "@/lib/supabaseClient";

/** Base del servicio de video en Cloudflare (Worker `vivelo-media` + bucket R2). */
export const MEDIA_URL = ((import.meta.env.VITE_MEDIA_URL as string | undefined) ?? "https://vivelo-media.quinterowilm.workers.dev").replace(/\/$/, "");

export type MediaKind = "community" | "webinar" | "thumb";
export type UploadResult = { url: string; key: string; durationSeconds: number | null };

export class UploadCancelled extends Error {
  constructor() {
    super("Subida cancelada");
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Tu sesión expiró. Vuelve a iniciar sesión.");
  return { authorization: `Bearer ${token}` };
}

async function api<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${MEDIA_URL}${path}`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(await authHeader()) },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Error ${res.status} del servicio de video`);
  return data;
}

function putPart(url: string, chunk: Blob, headers: Record<string, string>, onBytes: (loaded: number) => void, signal: AbortSignal): Promise<{ partNumber: number; etag: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => onBytes(e.loaded);
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText) as { partNumber?: number; etag?: string; error?: string };
        if (xhr.status >= 200 && xhr.status < 300 && data.etag && data.partNumber) resolve({ partNumber: data.partNumber, etag: data.etag });
        else reject(new Error(data.error ?? `Error ${xhr.status} subiendo una parte`));
      } catch {
        reject(new Error(`Respuesta inesperada (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error("Se cortó la conexión"));
    xhr.onabort = () => reject(new UploadCancelled());
    signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(chunk);
  });
}

/** Lee la duración de un archivo de video en el navegador (si el formato lo permite). */
export function readVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    if (!file.type.startsWith("video/")) return resolve(null);
    const v = document.createElement("video");
    const src = URL.createObjectURL(file);
    const done = (d: number | null) => {
      URL.revokeObjectURL(src);
      resolve(d);
    };
    v.preload = "metadata";
    v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? Math.round(v.duration) : null);
    v.onerror = () => done(null);
    window.setTimeout(() => done(null), 8000);
    v.src = src;
  });
}

/**
 * Sube un archivo a Cloudflare R2 por partes de 16 MB (con reintentos) y devuelve el enlace público.
 * onProgress recibe un número de 0 a 100.
 */
export async function uploadMedia(file: File, kind: MediaKind, onProgress: (pct: number) => void, signal: AbortSignal): Promise<UploadResult> {
  const durationPromise = readVideoDuration(file);
  const init = await api<{ key: string; uploadId: string; partSize: number }>("/upload/init", {
    kind,
    filename: file.name,
    contentType: file.type || "video/mp4",
    size: file.size,
  });
  const total = Math.ceil(file.size / init.partSize);
  const parts: { partNumber: number; etag: string }[] = [];
  let doneBytes = 0;
  try {
    for (let n = 1; n <= total; n++) {
      const chunk = file.slice((n - 1) * init.partSize, Math.min(file.size, n * init.partSize));
      let attempt = 0;
      for (;;) {
        try {
          const headers = await authHeader();
          const part = await putPart(
            `${MEDIA_URL}/upload/part?key=${encodeURIComponent(init.key)}&uploadId=${encodeURIComponent(init.uploadId)}&part=${n}`,
            chunk,
            headers,
            (loaded) => onProgress(Math.min(99, Math.round(((doneBytes + loaded) / file.size) * 100))),
            signal
          );
          parts.push(part);
          break;
        } catch (err) {
          if (err instanceof UploadCancelled || signal.aborted) throw new UploadCancelled();
          attempt += 1;
          if (attempt >= 3) throw err;
          await new Promise((r) => setTimeout(r, 800 * attempt));
        }
      }
      doneBytes += chunk.size;
      onProgress(Math.min(99, Math.round((doneBytes / file.size) * 100)));
    }
    const done = await api<{ key: string; url: string }>("/upload/complete", { key: init.key, uploadId: init.uploadId, parts });
    onProgress(100);
    return { url: done.url, key: done.key, durationSeconds: await durationPromise };
  } catch (err) {
    void api("/upload/abort", { key: init.key, uploadId: init.uploadId }).catch(() => {});
    throw err;
  }
}

/** Borra un archivo de R2 si el enlace es de nuestro servicio (si no, no hace nada). */
export async function deleteMediaByUrl(url: string | null | undefined): Promise<void> {
  if (!url || !url.startsWith(`${MEDIA_URL}/v/`)) return;
  const key = url.slice(`${MEDIA_URL}/v/`.length);
  await fetch(`${MEDIA_URL}/v/${key}`, { method: "DELETE", headers: await authHeader() }).catch(() => {});
}

export function isOwnMedia(url: string | null | undefined): boolean {
  return !!url && url.startsWith(`${MEDIA_URL}/v/`);
}
